import React from 'react';
import { ExternalLink } from 'lucide-react';
import { PayPeriod, LineItem, CommissionModel } from '../types';
import { formatCurrency, formatDateRange, formatDate } from '../utils/formatting';
interface ResizableInnerTableProps {
  columns: Column[];
  items: LineItem[];
  footer?: React.ReactNode;
}
// Shared table body used by all dense Closed Loan / Override / Referral / Recruiting / Expense tables.
function ResizableInnerTable({
  columns,
  items,
  footer
}: ResizableInnerTableProps) {
  return <div className="overflow-x-auto" data-id="element-27">
      <table className="w-full text-sm" data-id="element-28">
        <thead className="bg-white border-b border-slate-100" data-id="element-29">
          <tr data-id="element-30">
            {columns.map(col => <th key={col.key} className={`px-2.5 py-2 font-medium text-slate-500 text-[11px] uppercase tracking-wide whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-31">
                {col.label}
              </th>)}
          </tr>
        </thead>
        <tbody data-id="element-32">
          {items.map(item => <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-33">
              {columns.map(col => <td key={col.key} className={`px-2.5 py-2 whitespace-nowrap text-xs ${col.align === 'right' ? 'text-right text-slate-700' : 'text-slate-700'}`} data-id="element-34">
                  {col.render(item)}
                </td>)}
            </tr>)}
        </tbody>
        {footer}
      </table>
    </div>;
}
// Default subtotal footer used by most tables. Each table passes the amount and any styling overrides.
function SubtotalFooter({
  columns,
  amountNode
}: {
  columns: Column[];
  amountNode: React.ReactNode;
}) {
  return <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-35">
      <tr data-id="element-36">
        <td colSpan={Math.max(1, columns.length - 1)} className="px-2.5 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-37">
          Subtotal
        </td>
        <td className="px-2.5 py-2 text-right text-sm font-semibold whitespace-nowrap" data-id="element-38">
          {amountNode}
        </td>
      </tr>
    </tfoot>;
}
interface ExpandedRowProps {
  payPeriods: PayPeriod[];
  commissionModel: CommissionModel;
}
const SECTIONS: {
  type: string;
  label: string;
  isExpense?: boolean;
}[] = [{
  type: 'Payment',
  label: 'Closed Loans'
}, {
  type: 'Manager Override',
  label: 'Manager Override'
}, {
  type: 'Referral Payment',
  label: 'Referral Payment'
}, {
  type: 'Recruiting Bonus',
  label: 'Recruiting Bonus'
}, {
  type: 'Expense',
  label: 'Expense',
  isExpense: true
}];
interface Column {
  key: string;
  label: string;
  align?: 'left' | 'right';
  render: (item: LineItem) => React.ReactNode;
}
function buildClosedLoanColumns(commissionModel: CommissionModel): Column[] {
  const opportunityCol: Column = {
    key: 'opportunity',
    label: 'Opportunity',
    align: 'left',
    render: item => <a href="#" onClick={e => {
      e.preventDefault();
      console.log('Open opportunity:', item.opportunityId);
    }} className="text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap" data-id="element-39">
        {item.opportunityName}
      </a>
  };
  const sharedLeading: Column[] = [opportunityCol, {
    key: 'closedDate',
    label: 'Closed Date',
    render: i => i.closedDate ? formatDate(i.closedDate) : '—'
  }, {
    key: 'propertyState',
    label: 'State',
    render: i => i.propertyState ?? '—'
  }, {
    key: 'totalLoanAmount',
    label: 'Loan Amount',
    align: 'right',
    render: i => formatCurrency(i.loanAmount)
  }, {
    key: 'loanPurpose',
    label: 'Purpose',
    render: i => i.loanPurpose ?? '—'
  }, {
    key: 'lender',
    label: 'Lender',
    render: i => i.lender ?? '—'
  }, {
    key: 'compensationType',
    label: 'Comp Type',
    render: i => i.compensationType ?? '—'
  }, {
    key: 'loName',
    label: 'LO Name',
    render: i => i.loName ?? '—'
  }, {
    key: 'gross',
    label: 'Gross',
    align: 'right',
    render: i => i.gross != null ? formatCurrency(i.gross) : '—'
  }];
  if (commissionModel === 'Flat Fee') {
    return [...sharedLeading, {
      key: 'flat',
      label: 'Flat',
      align: 'right',
      render: i => i.flat != null ? formatCurrency(i.flat) : '—'
    }, {
      key: 'processing',
      label: 'Processing',
      align: 'right',
      render: i => i.processing != null ? formatCurrency(i.processing) : '—'
    }, {
      key: 'net',
      label: 'Net',
      align: 'right',
      render: i => i.net != null ? formatCurrency(i.net) : '—'
    }, {
      key: 'loSplit',
      label: 'LO Split',
      align: 'right',
      render: i => i.loSplit != null ? `${i.loSplit}%` : '—'
    }, {
      key: 'payroll',
      label: 'Payroll',
      align: 'right',
      render: i => <span className="font-semibold text-slate-900" data-id="element-40">
            {i.payroll != null ? formatCurrency(i.payroll) : '—'}
          </span>
    }];
  }
  // Standard
  return [...sharedLeading, {
    key: 'corpFee',
    label: 'Corp Fee',
    align: 'right',
    render: i => i.corpFee != null ? formatCurrency(i.corpFee) : '—'
  }, {
    key: 'net',
    label: 'Net',
    align: 'right',
    render: i => i.net != null ? formatCurrency(i.net) : '—'
  }, {
    key: 'loSplit',
    label: 'LO Split',
    align: 'right',
    render: i => i.loSplit != null ? `${i.loSplit}%` : '—'
  }, {
    key: 'lo',
    label: 'LO',
    align: 'right',
    render: i => i.lo != null ? formatCurrency(i.lo) : '—'
  }, {
    key: 'payroll',
    label: 'Payroll',
    align: 'right',
    render: i => <span className="font-semibold text-slate-900" data-id="element-41">
          {i.payroll != null ? formatCurrency(i.payroll) : '—'}
        </span>
  }, {
    key: 'balance',
    label: 'Balance',
    align: 'right',
    render: i => i.balance != null ? formatCurrency(i.balance) : '—'
  }];
}
interface ClosedLoansTableProps {
  items: LineItem[];
  commissionModel: CommissionModel;
}
function ClosedLoansTable({
  items,
  commissionModel
}: ClosedLoansTableProps) {
  if (items.length === 0) return null;
  const columns = buildClosedLoanColumns(commissionModel);
  const subtotal = items.reduce((sum, i) => sum + (i.payroll ?? i.amount), 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-42">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-43">
        <div className="flex items-center gap-2" data-id="element-44">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-45">Closed Loans</h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-46">
            {items.length}
          </span>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold" data-id="element-47">
            {commissionModel}
          </span>
        </div>
      </div>
      <div className="overflow-x-auto" data-id="element-48">
        <table className="w-full text-sm" data-id="element-49">
          <thead className="bg-white border-b border-slate-100" data-id="element-50">
            <tr data-id="element-51">
              {columns.map(col => <th key={col.key} className={`px-2.5 py-2 font-medium text-slate-500 text-[11px] uppercase tracking-wide whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-52">
                  {col.label}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-53">
            {items.map(item => <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-54">
                {columns.map(col => <td key={col.key} className={`px-2.5 py-2 whitespace-nowrap text-xs ${col.align === 'right' ? 'text-right text-slate-700' : 'text-slate-700'}`} data-id="element-55">
                    {col.render(item)}
                  </td>)}
              </tr>)}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-56">
            <tr data-id="element-57">
              <td colSpan={columns.length - 1} className="px-2.5 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-58">
                Subtotal
              </td>
              <td className="px-2.5 py-2 text-right text-sm font-semibold text-slate-900 whitespace-nowrap" data-id="element-59">
                {formatCurrency(subtotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>;
}
interface ManagerOverrideTableProps {
  items: LineItem[];
}
function ManagerOverrideTable({
  items
}: ManagerOverrideTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'opportunity',
    label: 'Loan',
    render: item => <a href="#" onClick={e => {
      e.preventDefault();
      console.log('Open opportunity:', item.opportunityId);
    }} className="text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap" data-id="element-60">
          {item.opportunityName}
        </a>
  }, {
    key: 'closedDate',
    label: 'Closed Date',
    render: i => i.closedDate ? formatDate(i.closedDate) : '—'
  }, {
    key: 'propertyState',
    label: 'Property State',
    render: i => i.propertyState ?? '—'
  }, {
    key: 'totalLoanAmount',
    label: 'Total Loan Amount',
    align: 'right',
    render: i => formatCurrency(i.loanAmount)
  }, {
    key: 'loanPurpose',
    label: 'Loan Purpose',
    render: i => i.loanPurpose ?? '—'
  }, {
    key: 'compensationType',
    label: 'Compensation Type',
    render: i => i.compensationType ?? '—'
  }, {
    key: 'loanOfficer',
    label: 'Loan Officer',
    render: i => i.loName ?? '—'
  }, {
    key: 'gross',
    label: 'Gross Commission',
    align: 'right',
    render: i => i.gross != null ? formatCurrency(i.gross) : '—'
  }, {
    key: 'flat',
    label: 'Flat Fee',
    align: 'right',
    render: i => i.flat != null ? formatCurrency(i.flat) : '—'
  }, {
    key: 'processing',
    label: 'Processing Fee',
    align: 'right',
    render: i => i.processing != null ? formatCurrency(i.processing) : '—'
  }, {
    key: 'net',
    label: 'Net Commission',
    align: 'right',
    render: i => i.net != null ? formatCurrency(i.net) : '—'
  }, {
    key: 'managerSplit',
    label: 'Manager Split',
    align: 'right',
    render: i => i.managerSplit != null ? `${i.managerSplit}%` : '—'
  }, {
    key: 'managerNet',
    label: 'Manager Net Commission',
    align: 'right',
    render: i => <span className="font-semibold text-slate-900" data-id="element-61">
          {i.managerNet != null ? formatCurrency(i.managerNet) : '—'}
        </span>
  }];
  const subtotal = items.reduce((sum, i) => sum + (i.managerNet ?? i.amount), 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-62">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-63">
        <div className="flex items-center gap-2" data-id="element-64">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-65">
            Manager Override
          </h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-66">
            {items.length}
          </span>
        </div>
      </div>
      <ResizableInnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-slate-900" data-id="element-69">{formatCurrency(subtotal)}</span>} data-id="element-68" />} data-id="element-67" />
    </div>;
}
interface RecruitingBonusTableProps {
  items: LineItem[];
}
function RecruitingBonusTable({
  items
}: RecruitingBonusTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'opportunity',
    label: 'Loan',
    render: item => <a href="#" onClick={e => {
      e.preventDefault();
      console.log('Open opportunity:', item.opportunityId);
    }} className="text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap" data-id="element-70">
          {item.opportunityName}
        </a>
  }, {
    key: 'closedDate',
    label: 'Closed Date',
    render: i => i.closedDate ? formatDate(i.closedDate) : '—'
  }, {
    key: 'propertyState',
    label: 'Property State',
    render: i => i.propertyState ?? '—'
  }, {
    key: 'loanPurpose',
    label: 'Loan Purpose',
    render: i => i.loanPurpose ?? '—'
  }, {
    key: 'loanOfficer',
    label: 'Loan Officer',
    render: i => i.loName ?? '—'
  }, {
    key: 'recruitingBonus',
    label: 'Recruiting Bonus',
    align: 'right',
    render: i => <span className="font-semibold text-slate-900" data-id="element-71">
          {formatCurrency(i.amount)}
        </span>
  }];
  const subtotal = items.reduce((sum, i) => sum + i.amount, 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-72">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-73">
        <div className="flex items-center gap-2" data-id="element-74">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-75">
            Recruiting Bonus
          </h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-76">
            {items.length}
          </span>
        </div>
      </div>
      <ResizableInnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-slate-900" data-id="element-79">{formatCurrency(subtotal)}</span>} data-id="element-78" />} data-id="element-77" />
    </div>;
}
interface ReferralProgramTableProps {
  items: LineItem[];
}
function ReferralProgramTable({
  items
}: ReferralProgramTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'opportunity',
    label: 'Loan',
    render: item => <a href="#" onClick={e => {
      e.preventDefault();
      console.log('Open opportunity:', item.opportunityId);
    }} className="text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap" data-id="element-80">
          {item.opportunityName}
        </a>
  }, {
    key: 'closedDate',
    label: 'Closed Date',
    render: i => i.closedDate ? formatDate(i.closedDate) : '—'
  }, {
    key: 'propertyState',
    label: 'Property State',
    render: i => i.propertyState ?? '—'
  }, {
    key: 'totalLoanAmount',
    label: 'Total Loan Amount',
    align: 'right',
    render: i => formatCurrency(i.loanAmount)
  }, {
    key: 'loa',
    label: 'LOA',
    render: i => i.loaName ?? '—'
  }, {
    key: 'gross',
    label: 'Gross Commission',
    align: 'right',
    render: i => i.gross != null ? formatCurrency(i.gross) : '—'
  }, {
    key: 'flat',
    label: 'Flat Fee',
    align: 'right',
    render: i => i.flat != null ? formatCurrency(i.flat) : '—'
  }, {
    key: 'processing',
    label: 'Processing Fee',
    align: 'right',
    render: i => i.processing != null ? formatCurrency(i.processing) : '—'
  }, {
    key: 'referralPayment',
    label: 'Referral Payment',
    align: 'right',
    render: i => <span className="font-semibold text-slate-900" data-id="element-81">
          {formatCurrency(i.amount)}
        </span>
  }];
  const subtotal = items.reduce((sum, i) => sum + i.amount, 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-82">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-83">
        <div className="flex items-center gap-2" data-id="element-84">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-85">
            Referral Program
          </h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-86">
            {items.length}
          </span>
        </div>
      </div>
      <ResizableInnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-slate-900" data-id="element-89">{formatCurrency(subtotal)}</span>} data-id="element-88" />} data-id="element-87" />
    </div>;
}
interface ExpenseTableProps {
  items: LineItem[];
}
function ExpenseTable({
  items
}: ExpenseTableProps) {
  if (items.length === 0) return null;
  const columns: Column[] = [{
    key: 'item',
    label: 'Item',
    render: i => <span className="font-medium text-slate-900 whitespace-nowrap" data-id="element-90">
          {i.itemName ?? i.opportunityName}
        </span>
  }, {
    key: 'expenseCategory',
    label: 'Expense Category',
    render: i => i.expenseCategory ? <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium" data-id="element-91">
            {i.expenseCategory}
          </span> : '—'
  }, {
    key: 'expenseDate',
    label: 'Expense Date',
    render: i => i.expenseDate ? formatDate(i.expenseDate) : '—'
  }, {
    key: 'expenseDetail',
    label: 'Expense Detail',
    render: i => <span className="text-slate-600" data-id="element-92">{i.expenseDetail ?? '—'}</span>
  }, {
    key: 'expenseAmount',
    label: 'Expense Amount',
    align: 'right',
    render: i => <span className="font-semibold text-red-600" data-id="element-93">
          −{formatCurrency(i.amount)}
        </span>
  }];
  const subtotal = items.reduce((sum, i) => sum + i.amount, 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-94">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-95">
        <div className="flex items-center gap-2" data-id="element-96">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-97">Expense</h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-98">
            {items.length}
          </span>
        </div>
      </div>
      <ResizableInnerTable columns={columns} items={items} footer={<SubtotalFooter columns={columns} amountNode={<span className="text-red-600" data-id="element-101">−{formatCurrency(subtotal)}</span>} data-id="element-100" />} data-id="element-99" />
    </div>;
}
interface SectionTableProps {
  label: string;
  items: LineItem[];
  isExpense?: boolean;
  showLoanAmount: boolean;
}
function SectionTable({
  label,
  items,
  isExpense,
  showLoanAmount
}: SectionTableProps) {
  if (items.length === 0) return null;
  const subtotal = items.reduce((sum, item) => sum + (isExpense ? -item.amount : item.amount), 0);
  return <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-102">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-103">
        <div className="flex items-center gap-2" data-id="element-104">
          <h4 className="text-sm font-semibold text-slate-900" data-id="element-105">{label}</h4>
          <span className="px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded-full text-xs font-medium" data-id="element-106">
            {items.length}
          </span>
        </div>
      </div>
      <table className="w-full text-sm" data-id="element-107">
        <thead className="bg-white border-b border-slate-100" data-id="element-108">
          <tr data-id="element-109">
            <th className="px-4 py-2 text-left font-medium text-slate-500 text-xs uppercase tracking-wide" data-id="element-110">
              {showLoanAmount ? 'Opportunity' : 'Description'}
            </th>
            {showLoanAmount && <th className="px-4 py-2 text-right font-medium text-slate-500 text-xs uppercase tracking-wide" data-id="element-111">
                Loan Amount
              </th>}
            <th className="px-4 py-2 text-right font-medium text-slate-500 text-xs uppercase tracking-wide" data-id="element-112">
              Amount
            </th>
          </tr>
        </thead>
        <tbody data-id="element-113">
          {items.map(item => <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-114">
              <td className="px-4 py-2" data-id="element-115">
                <a href="#" onClick={e => {
              e.preventDefault();
              console.log('Open opportunity:', item.opportunityId);
            }} className="text-blue-600 hover:text-blue-700 hover:underline" data-id="element-116">
                  {item.opportunityName}
                </a>
              </td>
              {showLoanAmount && <td className="px-4 py-2 text-right text-slate-600" data-id="element-117">
                  {item.loanAmount ? formatCurrency(item.loanAmount) : '—'}
                </td>}
              <td className={`px-4 py-2 text-right font-semibold ${isExpense ? 'text-red-600' : 'text-slate-900'}`} data-id="element-118">
                {isExpense ? '−' : ''}
                {formatCurrency(item.amount)}
              </td>
            </tr>)}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-119">
          <tr data-id="element-120">
            <td colSpan={showLoanAmount ? 2 : 1} className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-121">
              Subtotal
            </td>
            <td className={`px-4 py-2 text-right text-sm font-semibold whitespace-nowrap ${isExpense ? 'text-red-600' : 'text-slate-900'}`} data-id="element-122">
              {isExpense && subtotal !== 0 ? '−' : ''}
              {formatCurrency(Math.abs(subtotal))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>;
}
export function ExpandedRow({
  payPeriods,
  commissionModel
}: ExpandedRowProps) {
  return <div className="px-6 py-4 bg-slate-50 border-t border-slate-200" data-id="element-123">
      <div className="space-y-8" data-id="element-124">
        {payPeriods.map(period => {
        const closedLoans = period.lineItems.filter(li => li.paymentType === 'Payment');
        const managerOverrides = period.lineItems.filter(li => li.paymentType === 'Manager Override');
        const expenses = period.lineItems.filter(li => li.paymentType === 'Expense');
        const referrals = period.lineItems.filter(li => li.paymentType === 'Referral Payment');
        const recruiting = period.lineItems.filter(li => li.paymentType === 'Recruiting Bonus');
        const otherSections = SECTIONS.filter(s => s.type !== 'Payment' && s.type !== 'Manager Override' && s.type !== 'Expense' && s.type !== 'Referral Payment' && s.type !== 'Recruiting Bonus').map(section => ({
          ...section,
          items: period.lineItems.filter(li => li.paymentType === section.type)
        }));
        const totalNetCommission = period.lineItems.reduce((sum, li) => li.paymentType === 'Expense' ? sum - li.amount : sum + li.amount, 0);
        const totalPayrollAmount = totalNetCommission;
        return <div key={period.id} className="space-y-3" data-id="element-125">
              <div className="flex items-center gap-3 flex-wrap" data-id="element-126">
                <span className="px-3 py-1 bg-slate-200 text-slate-700 rounded-full text-sm font-semibold" data-id="element-127">
                  {formatDateRange(period.startDate, period.endDate)}
                </span>
                <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm" data-id="element-128">
                  Closed Units:{' '}
                  <span className="font-semibold" data-id="element-129">{period.units}</span>
                </span>
                <span className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm" data-id="element-130">
                  Closed Loan Volume:{' '}
                  <span className="font-semibold" data-id="element-131">
                    {formatCurrency(period.loanAmount)}
                  </span>
                </span>
                <a href="#" onClick={e => {
              e.preventDefault();
              console.log('Open pay period:', period.id);
            }} className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm" aria-label="Open pay period" data-id="element-132">
                  <ExternalLink className="w-4 h-4" data-id="element-133" />
                </a>
              </div>

              <div className="space-y-3" data-id="element-134">
                <ClosedLoansTable items={closedLoans} commissionModel={commissionModel} data-id="element-135" />
                <ManagerOverrideTable items={managerOverrides} data-id="element-136" />
                <ReferralProgramTable items={referrals} data-id="element-137" />
                <RecruitingBonusTable items={recruiting} data-id="element-138" />
                {otherSections.map(section => <SectionTable key={section.type} label={section.label} items={section.items} isExpense={section.isExpense} showLoanAmount={false} data-id="element-139" />)}
                <ExpenseTable items={expenses} data-id="element-140" />
              </div>

              {/* Totals */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mt-3" data-id="element-141">
                <div className="divide-y divide-slate-100" data-id="element-142">
                  <div className="flex items-center justify-between px-4 py-3" data-id="element-143">
                    <span className="text-sm font-medium text-slate-600" data-id="element-144">
                      Total Net Commission
                    </span>
                    <span className="text-sm font-bold text-slate-900" data-id="element-145">
                      {formatCurrency(totalNetCommission)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50" data-id="element-146">
                    <span className="text-sm font-semibold text-slate-700" data-id="element-147">
                      Total Payroll Amount
                    </span>
                    <span className="text-base font-bold text-slate-900" data-id="element-148">
                      {formatCurrency(totalPayrollAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}